import { Chip, Divider, Icon, PanelItem, colorValue } from '@hai/ui-react';
import React, { MouseEvent, ReactNode, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import defaultThumbnail from 'src/images/thumbnail-default.jpg'; // FIXME cache or pre-cache. When system goes offline it tries to fetch it again and appears as not found image.
import { useAdaptiveFetch } from 'src/js/hook/use-adaptive-fetch';
import { useStores } from 'src/js/hook/use-stores';
import { StatsInterval } from 'src/js/model/api/request/stream-stat-request';
import { StreamSrtStatsResponse } from 'src/js/model/api/response/stream-srt-stats-response';
import { LineChart } from 'src/js/pages/statistic/plot/line-chart';
import { mapStreamSRTStatisticToPlotData } from 'src/js/pages/statistic/statistic-mapper';
import {
  AudioEncoderViewModel,
  getAudioInterfaceLabels,
} from 'src/js/pages/streaming/audio-encoder/audio-encoder-models';
import { formatUptime } from 'src/js/pages/streaming/stream/stream-helper';
import { getStreamUuid, StreamState, StreamViewModel } from 'src/js/pages/streaming/stream/stream-models';
import {
  streamListStreamName,
  streamStateMessage,
  streamStateToColor,
} from 'src/js/pages/streaming/stream/stream-view-model-mapper';
import { VideoEncoderState } from 'src/js/pages/streaming/video-encoder/video-encoder-models';
import videoEncoders from 'src/js/pages/streaming/video-encoder/video-encoder-store';
import { constant } from 'src/js/constant';
import { useLocalStorage } from 'usehooks-ts';
import { StreamPreference, StreamPreferenceEnum } from '../streaming/stream/stream-preference';

import { fetchStreamSRTGraph } from 'src/js/pages/streaming/stream/stream-store';
import { generateDataAuto } from 'src/js/util/automation';
import { useMutable } from 'src/js/hook/use-mutable';
import { AudioMeter } from 'src/js/component/base/audio-meter';
import audioEncoders from '../streaming/audio-encoder/audio-encoder-store';
import { isChildCropped } from 'src/js/util/is-child-cropped';
import { findFirstParentMatching } from 'src/js/util/find-first-parent-matching';
import { OverflowScroller } from 'src/js/component/overflow-scroller';
import { RecordingIcon } from 'src/js/component/recording-icon';

const FormatRoundTripTimeInMS = (rtt: number) => (rtt < 1000 ? '< 1ms' : `${Math.round(rtt / 1000)}ms`);
const AUDIO_METERS_SCROLL_EPSILON = 0.5;

export interface DecoderStatisticsParameters {
  account: string;
}

export interface Props {
  model: StreamViewModel;
  onClearStats?: (model: StreamViewModel) => void;
}

export const DashboardStreamPanel: React.FunctionComponent<Props> = ({ model, onClearStats, ...rest }) => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const uuid = getStreamUuid(model);
  // const panelRef = useRef(null);
  const streamPref = useRef<StreamPreference>(null);
  if (streamPref.current === null) {
    streamPref.current = new StreamPreference(uuid, sessionStore.username);
  }
  const audioMeters = useRef<HTMLDivElement | null>(null);
  const setAudioMetersRef = useCallback((node: HTMLDivElement | null) => {
    audioMeters.current = node;
  }, []);
  const overflowScroller = useRef<HTMLDivElement | null>(null);
  const setOverflowScrollerRef = useCallback((node: HTMLDivElement | null) => {
    overflowScroller.current = node;
  }, []);

  const navigate = useNavigate();
  const videoEncoder = videoEncoders.find((v) => (model.video ? v.id === model.video[0]?.id : false));

  let audioChips: Partial<AudioEncoderViewModel>[] = [];
  if (model.audio?.length > 0) {
    audioChips = model.audio?.map((audenc, _index) => {
      return {
        ...audenc,
        interfaceTxt: getAudioInterfaceLabels(audenc.interface, audenc.layout, audenc.codec).interfaceStr,
      };
    });
  }

  const metadataSources = model.metadata;
  const scteSources = model.scte;

  const handleClickVideoEncoder = (event: MouseEvent, id: number) => {
    event.stopPropagation();
    navigate('/streaming/videnc/', { state: { id: id, stats: true } });
  };

  const handleClickAudioEncoder = (event: MouseEvent, id: number) => {
    event.stopPropagation();
    navigate('/streaming/audenc/', { state: { id: id, stats: true } });
  };

  const handleClickMetadata = (event: MouseEvent, id: number) => {
    event.stopPropagation();
    navigate('/streaming/metadata/', { state: { id: id, stats: true } });
  };

  const handleClickScte = (event: MouseEvent, id: number) => {
    event.stopPropagation();
    navigate('/streaming/scte/', { state: { id: id, stats: true } });
  };

  const handleClickStream = () => {
    navigate('/streaming/stream/', { state: { id: model?.id, stats: true } });
  };

  const showDetailedStats = () => {
    navigate(`/streaming/stream/stats/${model?.id}`);
  };

  // thumbnail refresh
  const [thumbnailDate, setThumbnailDate] = useState<string>(`${new Date().getTime()}`);
  useEffect(() => {
    let endrun = false;
    let timer: any;
    const refreshRate = (videoEncoder?.previewIntervalSec ?? 10) * 1000;
    const triggerRefresh = () => {
      if (!endrun) {
        setThumbnailDate(`${new Date().getTime()}`);
        if (videoEncoder && videoEncoder.previewIntervalSec >= 1) {
          timer = setTimeout(triggerRefresh, refreshRate);
        }
      }
    };

    timer = setTimeout(() => {
      triggerRefresh();
    }, refreshRate);

    return () => {
      // cleanup
      endrun = true;
      clearTimeout(timer);
    };
  }, [videoEncoder?.previewIntervalSec]);

  const snapshotRef = useRef<HTMLImageElement>();
  const [defaultThumbnailHidden, setDefaultThumbnailHidden] = useState(false);
  const onLoadThumbnail = (e: SyntheticEvent<HTMLImageElement, Event>) => {
    if (e.type !== 'error' && snapshotRef?.current) {
      snapshotRef.current.style.visibility = 'visible';
      setDefaultThumbnailHidden(true); // show real image
      return;
    }
    setDefaultThumbnailHidden(false);
  };

  useEffect(() => {
    const cond1 = model.state !== StreamState.RUNNING || !videoEncoder?.previewEnabled;
    const cond2 =
      model.state === StreamState.RUNNING &&
      videoEncoder?.previewEnabled &&
      videoEncoder?.stats.state !== VideoEncoderState.WORKING;
    if (cond1 || cond2) {
      setDefaultThumbnailHidden(false); // show status text with empty/default static background
    }
  }, [model.state, videoEncoder?.previewEnabled, videoEncoder?.stats?.state]);

  const [graphHeight, setGraphHeight] = useState(120);
  const [graphContainerHeight, setGraphContainerHeight] = useState(125);
  const [displaySideBySide, setDisplaySideBySide] = useState(true);
  const [displayFullInfo, setDisplayFullInfo] = useState(true);
  const [isAudioMetersOverflowing, setIsAudioMetersOverflowing] = useState(false);
  const [audioMetersScrollOffset, setAudioMetersScrollOffset] = useState(0);
  const audioMetersScrollOffsetRef = useRef(0);
  const audioMetersScrollRafRef = useRef<number | null>(null);

  useEffect(() => {
    audioMetersScrollOffsetRef.current = audioMetersScrollOffset;
  }, [audioMetersScrollOffset]);

  useEffect(() => {
    return () => {
      if (audioMetersScrollRafRef.current !== null) {
        cancelAnimationFrame(audioMetersScrollRafRef.current);
      }
    };
  }, []);

  const getAudioMetersMaxScrollOffset = () => {
    const audioMetersElement = audioMeters.current;
    if (!audioMetersElement) {
      return 0;
    }

    const parentPanelContent = findFirstParentMatching(audioMetersElement, '.panel-content');
    if (!parentPanelContent) {
      return 0;
    }

    const metersLeftInset = Math.max(0, audioMetersElement.offsetLeft || 0);
    const visibleMetersWidth = Math.max(0, parentPanelContent.clientWidth - metersLeftInset);
    const overflowWidth = audioMetersElement.scrollWidth - visibleMetersWidth;
    return Math.max(0, overflowWidth);
  };

  const getAudioMetersScrollStep = () => {
    const audioMetersElement = audioMeters.current;
    if (!audioMetersElement) {
      return 0;
    }

    const parentPanelContent = findFirstParentMatching(audioMetersElement, '.panel-content');
    if (!parentPanelContent) {
      return 0;
    }

    const contentWidth = audioMetersElement.scrollWidth;
    const metersLeftInset = Math.max(0, audioMetersElement.offsetLeft || 0);
    const availableWidth = Math.max(0, parentPanelContent.clientWidth - metersLeftInset);
    if (contentWidth <= 0 || availableWidth <= 0 || contentWidth <= availableWidth) {
      return 0;
    }

    const pageCount = Math.max(2, Math.ceil(contentWidth / availableWidth));
    const maxOffset = Math.max(0, contentWidth - availableWidth);
    return maxOffset / (pageCount - 1);
  };

  const handleAudioMetersResize = () => {
    const parentPanelContent = findFirstParentMatching(audioMeters.current, '.panel-content');
    const _isAudioMetersOverflowing = isChildCropped(parentPanelContent, audioMeters.current);
    setIsAudioMetersOverflowing((previousValue) =>
      previousValue === _isAudioMetersOverflowing ? previousValue : _isAudioMetersOverflowing,
    );
    setAudioMetersScrollOffset((previousOffset) => {
      const clampedOffset = Math.min(previousOffset, getAudioMetersMaxScrollOffset());
      return previousOffset === clampedOffset ? previousOffset : clampedOffset;
    });
  };

  // horizontal scroll animation
  const animateAudioMetersScrollTo = (targetOffset: number) => {
    if (audioMetersScrollRafRef.current !== null) {
      cancelAnimationFrame(audioMetersScrollRafRef.current);
    }

    const startOffset = audioMetersScrollOffsetRef.current;
    const endOffset = Math.max(0, Math.min(getAudioMetersMaxScrollOffset(), targetOffset));

    if (startOffset === endOffset) {
      return;
    }

    const durationMs = 420;
    const startTime = performance.now();

    // scroll animation progress
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextOffset = startOffset + (endOffset - startOffset) * eased;

      setAudioMetersScrollOffset(nextOffset);

      if (progress < 1) {
        audioMetersScrollRafRef.current = requestAnimationFrame(tick);
      } else {
        audioMetersScrollRafRef.current = null;
        setAudioMetersScrollOffset(endOffset);
      }
    };

    audioMetersScrollRafRef.current = requestAnimationFrame(tick);
  };

  const scrollMetersLeft = () => {
    const scrollStep = getAudioMetersScrollStep();
    animateAudioMetersScrollTo(audioMetersScrollOffsetRef.current - scrollStep);
  };

  const scrollMetersRight = () => {
    const scrollStep = getAudioMetersScrollStep();
    animateAudioMetersScrollTo(audioMetersScrollOffsetRef.current + scrollStep);
  };

  const maxScrollOffset = getAudioMetersMaxScrollOffset();
  const canScrollLeft = audioMetersScrollOffset > AUDIO_METERS_SCROLL_EPSILON;
  const canScrollRight = audioMetersScrollOffset < maxScrollOffset - AUDIO_METERS_SCROLL_EPSILON;

  const handleResize = (_height?: number) => {
    // get height directly from element
    const panelElement = document.getElementById(`streamPanel-${model.id}`);
    // not yet supported in haiui to put ref const panelElement = panelRef.current?.nativeElement;
    if (!panelElement) {
      return;
    }
    const height = panelElement.clientHeight;
    const graphContainerHeight = 0.5 * height;
    const graphHeight = graphContainerHeight + 75;

    setDisplayFullInfo(height >= 240);
    setDisplaySideBySide(height > 250 ? true : false);
    //SetGraphHeight Depending of the zooming and size fluctuation
    //TODO : Find a math function to optimize reize handling without using random numbers/thresholds
    if (height > 1000) {
      setGraphHeight(Math.round(graphHeight / 3));
    } else if (500 < height && height < 1000) {
      setGraphHeight(Math.round(graphHeight / 1.75));
      setGraphContainerHeight(Math.round(graphContainerHeight));
    } else if (500 > height && height > 375) {
      setGraphHeight(Math.round(graphHeight / 1.5));
      setGraphContainerHeight(Math.round(graphContainerHeight / 1.25));
    } else if (height < 375 && height > 250) {
      setGraphHeight(Math.round(graphHeight));
      setGraphContainerHeight(Math.round(graphContainerHeight / 1.1));
    } else if (height < 250) {
      setGraphHeight(Math.round(graphHeight) / 2);
      setGraphContainerHeight(Math.round(graphContainerHeight / 1.25));
    }
  };

  const imageRef = useRef<HTMLImageElement>();

  const [graphShown, setGraphShown] = useState(false);
  const [graphShownByClick, setGraphShownByClick] = useMutable(false);
  const [graphShownOnHover, setGraphShownOnHover] = useMutable<boolean | number>(false);

  useEffect(() => {
    const panelElement = document.querySelector('.streamPanel');
    if (panelElement) {
      const panelHeight = panelElement.clientHeight;
      handleResize(panelHeight);
    } else {
      /*eslint-disable-next-line no-console */
      console.error('streamPanel element not found');
    }
  }, [imageRef, graphShown]);

  const [graphData, setGraphData] = useState<StreamSrtStatsResponse[]>([]);
  const GraphDelay: React.FunctionComponent = () => (
    <LineChart
      subTitle={t('statistics.chart.delayTitle')}
      yTickCount={2}
      fontSize={displaySideBySide ? 18 : null}
      padding={{
        top: 21,
        right: 0,
        bottom: displaySideBySide ? 22 : 16,
        left: displaySideBySide ? 60 : 50,
      }}
      data={mapStreamSRTStatisticToPlotData(graphData, ['RTT'], t)}
      timescale={StatsInterval.LAST_5_MINS}
      xAxisMinutes={true}
      lineColors={[colorValue('haiui-amber-01')]}
      height={graphHeight}
      showLegend={false}
      dashboard={true}
      pathSelected={['path1']}
      tooltips={false}
    />
  );

  const GraphBandwidth = () => (
    <LineChart
      subTitle={t('statistics.chart.bandwidthTitle')}
      yTickCount={2}
      fontSize={displaySideBySide ? 18 : null}
      padding={{
        top: 21,
        right: 0,
        bottom: displaySideBySide ? 22 : 16,
        left: displaySideBySide ? 70 : 50,
      }}
      data={mapStreamSRTStatisticToPlotData(graphData, ['SendRate'], t)}
      timescale={StatsInterval.LAST_5_MINS}
      xAxisMinutes={true}
      lineColors={[constant.productColor]}
      height={graphHeight}
      showLegend={false}
      dashboard={true}
      pathSelected={['path1']}
      tooltips={false}
    />
  );

  const [displayGraphRefreshRate, setDisplayGraphRefreshRate] = useState<number | null>();
  useEffect(() => {
    setDisplayGraphRefreshRate(graphShown ? 5000 : null);
  }, [graphShown]);

  useAdaptiveFetch(
    () => {
      return fetchStreamSRTGraph(model, StatsInterval.LAST_5_MINS, undefined, (updated) => {
        setGraphData(updated);
      });
    },
    displayGraphRefreshRate,
    [displayGraphRefreshRate],
  );
  const assignedAudioEnccderIds = model.audio?.map((a) => a.id);
  const assignedAudioEncoders = audioEncoders.filter((a) => assignedAudioEnccderIds.includes(a.id));
  const audioEncoder = assignedAudioEncoders?.[0];
  const allPairs = assignedAudioEncoders.reduce((pairs, encoder) => pairs.concat(encoder.stats?.audioPairs ?? []), []);
  const showSlidingPanelWithGraph = model.stats?.state === StreamState.RUNNING && model.stats?.srt;

  const displayGraphs = () => {
    if (!showSlidingPanelWithGraph) {
      return null;
    }

    if (!graphShown) {
      return <div />;
    }

    return displaySideBySide ? (
      <div className="side-by-side-graphs">
        <GraphDelay />
        <GraphBandwidth />
      </div>
    ) : (
      <div>
        <GraphDelay />
      </div>
    );
  };

  const thumbnailWidth = 400; // FIXME
  const previewEnabled = true;
  let barWidth = 0;
  let emptyBars = previewEnabled ? 0 : 6 - allPairs?.length;
  if (!previewEnabled && thumbnailWidth > 250) {
    emptyBars += 2; // if preview disabled and we have space, add empty bars
  }
  if (!previewEnabled && thumbnailWidth > 400) {
    emptyBars += 2; // if preview disabled and we have space, add empty bars
  }
  // console.log('thumbnail width', thumbnailWidth)
  if (isNaN(emptyBars)) {
    emptyBars = 0;
  }

  const AudioMeters: React.FunctionComponent<{ bottomSpacing: number }> = ({ bottomSpacing }) => {
    return (
      /*thumbnailWidth === 0 ? null :*/
      <div
        data-auto="audio_meter"
        className="audio-meters d-flex"
        ref={setAudioMetersRef}
        style={{ transform: `translateX(-${audioMetersScrollOffset}px)`, bottom: 12 + bottomSpacing }}
      >
        <>
          {allPairs?.map((meter, index) => {
            let width = Math.min(
              34,
              (thumbnailWidth - 10) / Math.min(audioEncoder?.stats.audioPairsAmount + emptyBars, 10) - 10,
            );
            if (meter.center != null) {
              width /= 2;
              width += 3;
            }
            if (width % 2 !== 0) {
              width++; // make even
            }
            barWidth = width;
            return <AudioMeter key={index} channel={index + 1} meter={meter} width={width} />;
          })}
          {[...Array(emptyBars)].map((_id, index) => {
            return <AudioMeter key={index} channel={index + 1} meter={null} width={barWidth} />;
          })}
        </>
      </div>
    );
  };
  const slidingPanelItems = () => {
    if (model.stats?.state !== StreamState.RUNNING) {
      return null;
    }

    if (model.stats?.srt) {
      const data = [
        {
          label: t('dashboard.statistics.rtt'),
          tooltip: t('dashboard.statistics.rttExplain'),
          text: FormatRoundTripTimeInMS(model.stats?.srt?.rtt),
          color: 'haiui-amber-01',
        },
        {
          label: t('dashboard.statistics.bitrate'),
          tooltip: t('dashboard.statistics.bitrateExplain'),
          text: model.stats.bitrate.replace(' ', ''),
          color: constant.productColor,
        },
        {
          label: t('dashboard.statistics.dropped'),
          tooltip: t('dashboard.statistics.droppedExplain'),
          text: model.stats.srt.droppedPackets,
          color: 'haiui-gray-06',
        },
      ];

      if (displayFullInfo) {
        data.push({
          label: t('dashboard.statistics.latency'),
          tooltip: t('dashboard.statistics.latencyExplain'),
          text: `${model.stats.srt.latency}ms`,
          color: 'haiui-gray-06',
        });
      }
      return data;
    } else {
      return [
        {
          label: t('dashboard.statistics.bitrate'),
          tooltip: t('dashboard.statistics.bitrateExplain'),
          text: model.stats.bitrate.replace(' ', ''),
          color: 'haiui-gray-06',
        },
      ];
    }
  };

  const [mouseEnterGraphTimeOut, setMouseEnterGraphTimeOut] = useMutable<NodeJS.Timeout>(undefined);
  const [mouseOutGraphTimeOut, setMouseOutGraphTimeOut] = useMutable<NodeJS.Timeout>(undefined);
  const onMouseEnterThumbnail = (e: MouseEvent) => {
    // prevent onMouseEnter when hover the audio meter
    const target = e.target instanceof Node ? e.target : null;
    if (
      target &&
      ((audioMeters.current && audioMeters.current.contains(target)) ||
        (overflowScroller.current && overflowScroller.current.contains(target)))
    ) {
      // console.log('audio metter prevent');
      return;
    }
    // console.log('on mouse enter thumbnail', e.target, graphShownByClick(), graphShown);
    if (graphShownByClick() === true && graphShown === false) {
      return; // dont open
    }
    setMouseEnterGraphTimeOut(
      setTimeout(() => {
        clearTimeout(mouseOutGraphTimeOut());
        if (graphShownByClick() === false && showSlidingPanelWithGraph) {
          setGraphShown(true);
          setGraphShownOnHover(Date.now());
        }
      }, 500),
    );
  };
  const onMouseLeaveThumbnail = () => {
    clearTimeout(mouseOutGraphTimeOut());
  };

  const onMouseEnterPanel = () => {
    // setIsInsidePanel(true);
  };
  const onMouseLeavePanel = () => {
    // setIsInsidePanel(false);
    console.log('leave', graphShownOnHover(), mouseEnterGraphTimeOut);
    if (graphShownOnHover() !== false) {
      setMouseOutGraphTimeOut(
        setTimeout(() => {
          if (graphShownByClick() === false) {
            clearTimeout(mouseEnterGraphTimeOut());
            setGraphShown(false);
            setGraphShownOnHover(false);
          }
        }, 250),
      );
    } else {
      clearTimeout(mouseEnterGraphTimeOut());
    }
  };

  // hide for all
  const [hideAllSlidingPanel, setHideAllSlidingPanel] = useLocalStorage(
    `${constant.lStorage.hideAllSlidingPanel}:${sessionStore.username}`,
    false,
  );

  // hide individual
  const [hideSingleSlidingPanel, setStreamPreference] = streamPref.current.useStreamPreference<boolean | undefined>(
    StreamPreferenceEnum.hideSlidingPanel,
    undefined,
  );

  const [showAudioMeters, setShowAudioMeters] = useLocalStorage(
    `${constant.lStorage.showAudioMeters}:${sessionStore.username}`,
    true,
  );

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !showAudioMeters) {
      // console.error('failed to attach ResizeObserver');
      return () => undefined;
    }

    const audioMetersElement = audioMeters.current;
    if (!audioMetersElement) {
      // console.error('failed to attach ResizeObserver, no elements');
      return () => undefined;
    }

    const panelContentElement = findFirstParentMatching(audioMetersElement, '.panel-content');
    if (!panelContentElement) {
      /*eslint-disable-next-line no-console */
      console.error('failed to attach ResizeObserver, no parent');
      return () => undefined;
    }

    const observer = new ResizeObserver(() => {
      handleAudioMetersResize();
    });

    observer.observe(panelContentElement);
    handleAudioMetersResize();

    return () => {
      observer.disconnect();
    };
  }, [showAudioMeters, model.audio?.length, audioEncoder?.stats?.audioPairsAmount]);

  const menuItems = !sessionStore.isUser()
    ? model.stats?.srt
      ? [
          {
            title: t('dashboard.resetStatistics'),
            onSelect: () => onClearStats?.(model),
            selectable: false,
          },
          {
            title: t('dashboard.detailedStatistics'),
            onSelect: showDetailedStats,
            selectable: false,
          },
        ]
      : [
          {
            title: t('dashboard.resetStatistics'),
            onSelect: () => onClearStats?.(model),
            selectable: false,
          },
        ]
    : model.stats?.srt
    ? [{ title: t('dashboard.detailedStatistics'), onSelect: showDetailedStats, selectable: false }]
    : [];
  if (model.stats?.state === StreamState.RUNNING) {
    // single stream
    const singleStreamOfferShow =
      hideSingleSlidingPanel === true || (hideSingleSlidingPanel === undefined && hideAllSlidingPanel === true);
    menuItems.push({
      title: singleStreamOfferShow ? t('dashboard.showSlidingPanel') : t('dashboard.hideSlidingPanel'),
      onSelect: () => {
        if (singleStreamOfferShow) {
          // currently off, asking to show
          const hideSlidingPanel = false;
          setStreamPreference(hideSlidingPanel);
        } else {
          // asking to hide
          const hideSlidingPanel = true;
          setStreamPreference(hideSlidingPanel);
        }
      },
      selectable: false, // This doesnt work but will after HAIUI-1553
    });
    // all
    menuItems.push({
      title: singleStreamOfferShow ? t('dashboard.showSlidingPanelAll') : t('dashboard.hideSlidingPanelAll'),
      onSelect: () => {
        // unset individual preference, do this first so other panels will receive it first before the all-streams settings, because this one has higher priority.
        streamPref.current.clear(StreamPreferenceEnum.hideSlidingPanel);

        if (singleStreamOfferShow) {
          // currently hidden, toggling to show all
          // asking to show all
          setHideAllSlidingPanel(false);
        } else {
          // asking to hide all
          setHideAllSlidingPanel(true);
        }
      },
      selectable: false,
    });
  }

  menuItems.push({
    title: showAudioMeters ? t('dashboard.hideAudioChannels') : t('dashboard.showAudioChannels'),
    onSelect: () => setShowAudioMeters(!showAudioMeters),
    selectable: false,
  });

  let showSlidingPanel = true;
  if (hideAllSlidingPanel) {
    showSlidingPanel = false;
  }
  if (hideSingleSlidingPanel === true) {
    // single stream preference overrides global one
    showSlidingPanel = false;
  } else if (hideSingleSlidingPanel === false) {
    showSlidingPanel = true;
  }

  const statusColor = streamStateToColor(model.state);
  const streamMsg = streamStateMessage(model, videoEncoder, t);

  interface SubtitleProps {
    children?: ReactNode;
  }
  const SubtitleDivider: React.FC<SubtitleProps> = ({ children }) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {children}
      <Divider type="vertical" />
    </div>
  );

  const subtitleIcons = [
    <SubtitleDivider key="1">
      <span>{t('general.id')}</span>
      <Chip size="sm" onClick={handleClickStream}>
        {model.id}
      </Chip>
    </SubtitleDivider>,
    <SubtitleDivider key="2">
      <div className="tnum">{formatUptime(model.stats?.uptime)}</div>
    </SubtitleDivider>,
  ];

  if (videoEncoder) {
    subtitleIcons.push(
      <SubtitleDivider key="3">
        <div className="tnum">{videoEncoder?.outputResolution}</div>
      </SubtitleDivider>,
    );
    subtitleIcons.push(<Icon key="4" iconname="Video" size="sm3" />);
    subtitleIcons.push(
      <Chip
        size="sm"
        key="5"
        tooltip={
          <>
            <div>{videoEncoder?.interfaceTxt}</div>
            {videoEncoder?.name}
          </>
        }
        id={videoEncoder?.id}
        popoverBgColor={constant.productColor}
        onClick={handleClickVideoEncoder}
      >
        {videoEncoder?.id}
      </Chip>,
    );
  }

  if (audioChips?.length > 0) {
    subtitleIcons.push(<Divider key="6" type="vertical" />);
    subtitleIcons.push(<Icon key="7" iconname="AudioAlt" size="sm3" />);

    audioChips.map((a) => {
      subtitleIcons.push(
        <Chip
          key={`audio-${a.id}`}
          size="sm"
          tooltip={
            <>
              <div>{a.interfaceTxt}</div>
              {a.name}
            </>
          }
          id={a.id}
          popoverBgColor={constant.productColor}
          onClick={handleClickAudioEncoder}
        >
          {a.id}
        </Chip>,
      );
    });
  }
  if (metadataSources?.length > 0) {
    subtitleIcons.push(<Divider key="8" type="vertical" />);
    subtitleIcons.push(<Icon key="9" iconname="Metadata" size="sm3" />);
    metadataSources.map((a) => {
      subtitleIcons.push(
        <Chip
          key={`metadata-${a.id}`}
          size="sm"
          tooltip={
            <>
              {t('dashboard.metadata')}
              <div>{a?.name}</div>
            </>
          }
          id={a?.id}
          popoverBgColor={constant.productColor}
          onClick={handleClickMetadata}
        >
          {a?.id}
        </Chip>,
      );
    });
  }

  if (scteSources?.length > 0) {
    subtitleIcons.push(<Divider key="10" type="vertical" />);
    subtitleIcons.push(<Icon key="11" iconname="Device" size="sm3" />);
    scteSources.map((a) => {
      subtitleIcons.push(
        <Chip
          key={`scte-${a.id}`}
          size="sm"
          tooltip={
            <>
              {t('dashboard.scte')}
              <div>{a?.name}</div>
            </>
          }
          id={a?.id}
          popoverBgColor={constant.productColor}
          onClick={handleClickScte}
        >
          {a?.id}
        </Chip>,
      );
    });
  }

  return (
    <>
      <PanelItem
        panelColor={statusColor}
        className="streamPanel"
        id={`streamPanel-${model.id}`}
        onMouseEnter={onMouseEnterPanel}
        onMouseLeave={onMouseLeavePanel}
        // ref={panelRef} // not yet supported in HaiUI
        menuItems={menuItems}
        icon={<Icon iconname={model.statusIcon} style={{ fill: statusColor }} size="sm2" />}
        iconHover={<Icon iconname="ArrowThinRight" size="sm2" color="white" />}
        title={
          model.recording?.status === 'RECORDING' ? (
            <div className="mk-dashboard-panel-title">
              <div style={{ width: '67px', marginRight: '5px' }}>
                <RecordingIcon small size="62px" className="recording-overlay" />
              </div>
              <div>{streamListStreamName(t, model.name)}</div>
            </div>
          ) : (
            streamListStreamName(t, model.name)
          )
        }
        data-auto={generateDataAuto('panel', model.name)}
        useFsMask
        subtitle={subtitleIcons}
        slidingPanel={
          showSlidingPanel && model.stats?.state === StreamState.RUNNING
            ? {
                height: `${graphContainerHeight}px`,
                expandedContent: displayGraphs(),
                bottomRow: slidingPanelItems(),
                show: graphShown,
                onShow: (show: boolean) => {
                  const lastOpened = graphShownOnHover();
                  // console.log('closing manually', show);
                  if (show === false && lastOpened !== false) {
                    // if within 1 second, ignore click to close
                    const diff = (lastOpened as number) + 1000 - Date.now();
                    if (diff > 0) {
                      // too quick after hover open, ignore
                      // actually mark it to keep open by click
                      setGraphShown(true);
                      setGraphShownByClick(true);
                      clearTimeout(mouseEnterGraphTimeOut()); // clear timeout if any
                      setGraphShownOnHover(false);
                      return;
                    }
                  }
                  setGraphShown(show);
                  // if (show) { // enable this so we remember the user just closed it and disable the toggling
                  setGraphShownByClick(show);
                  // }
                  clearTimeout(mouseEnterGraphTimeOut()); // clear timeout if any
                },
              }
            : null
        }
        onClickTitle={handleClickStream}
        onResize={handleResize}
      >
        {model?.id && (
          <div onMouseEnter={onMouseEnterThumbnail} onMouseLeave={onMouseLeaveThumbnail}>
            {model.stats?.state === StreamState.RUNNING &&
              videoEncoder?.stats?.state === VideoEncoderState.WORKING &&
              videoEncoder?.previewEnabled && (
                <img
                  {...rest}
                  className="fs-mask"
                  src={`/apis/preview/${videoEncoder?.interface}?t=${thumbnailDate}`}
                  onError={onLoadThumbnail}
                  onLoad={onLoadThumbnail}
                  ref={snapshotRef}
                />
              )}
            <div className="thumbnail" hidden={defaultThumbnailHidden}>
              <img src={defaultThumbnail} ref={imageRef} />
              <div className="overlay unselectable haiui-heading-04-med">
                {(model.state !== StreamState.RUNNING ||
                  videoEncoder?.stats?.state !== VideoEncoderState.WORKING ||
                  !videoEncoder?.previewEnabled) &&
                  streamMsg}
              </div>
            </div>
            {(!showSlidingPanel || !graphShown) &&
              showAudioMeters &&
              model.state === StreamState.RUNNING &&
              audioEncoder?.stats?.audioPairsAmount > 0 && <AudioMeters bottomSpacing={showSlidingPanel ? 32 : 0} />}
            {(!showSlidingPanel || !graphShown) && showAudioMeters && isAudioMetersOverflowing && (
              <OverflowScroller
                ref={setOverflowScrollerRef}
                onLeft={scrollMetersLeft}
                onRight={scrollMetersRight}
                canScrollLeft={canScrollLeft}
                canScrollRight={canScrollRight}
                bottomSpacing={showSlidingPanel ? 32 : 0}
              />
            )}
          </div>
        )}
      </PanelItem>
    </>
  );
};
